import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";

import OrganizationUnit from "../../entities/OrganizationUnit";
import i18n from "../../i18n";
import { apiCallBegan, ApiCallFailedPayload } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import ResponseBase from "../types/ResponseBase";
import SliceCollectionBase from "../types/SliceCollectionBase";
import { authHeader } from "../users/auth";

// RESPONSE DECLARATIONS //
interface OrganizationUnitResponse extends ResponseBase {
  CompanyId: number;
  CompanyName: string;
  Name: string;
  Id: number;
}

// SLICE //
interface State extends SliceCollectionBase<OrganizationUnit> {}

const initialState: State = {
  list: [],
  loading: false,
  lastFetch: null,
  error: null
};

const slice = createSlice({
  name: "organizationUnits",
  initialState,
  reducers: {
    // ORGANIZATION UNITS
    organizationUnitsRequested: organizationUnits => {
      organizationUnits.loading = true;
      organizationUnits.error = null;
    },
    organizationUnitsReceived: (
      organizationUnits,
      action: PayloadAction<OrganizationUnit[]>
    ) => {
      organizationUnits.list = schema(action.payload);
      organizationUnits.loading = false;
      organizationUnits.lastFetch = Date.now();
    },
    organizationUnitsRequestFailed: (
      organizationUnits,
      action: PayloadAction<ApiCallFailedPayload>
    ) => {
      organizationUnits.loading = false;
      organizationUnits.error = action.payload;
      organizationUnits.lastFetch = Date.now();
    }
  }
});

const {
  organizationUnitsRequested,
  organizationUnitsReceived,
  organizationUnitsRequestFailed
} = slice.actions;

export default slice.reducer;

// ACTION CREATORS //
export const getOrganizationUnits =
  () => async (dispatch: AppDispatch, getState: () => AppState) => {
    const { lastFetch, loading } = getState().entities.organizationUnits;

    if (loading) {
      return;
    }

    const diffInMinutes = moment().diff(moment(lastFetch), "minutes");
    if (
      diffInMinutes <
        parseInt((window._env_.REACT_APP_COMMON_REQUEST_INTERVAL || process.env.REACT_APP_COMMON_REQUEST_INTERVAL)!, 10)
    ) {
      return;
    }

    const url = `/api/organization/units`;

    var result = await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: {
          ...authHeader(),
          "Accept-Language": i18n.language
        },
        onStart: organizationUnitsRequested.type,
        onSuccess: organizationUnitsReceived.type,
        onError: organizationUnitsRequestFailed.type
      })
    );
    return result;
  };

// NORMALIZATION //
const schema = (payload: OrganizationUnitResponse[]): OrganizationUnit[] => {
  if (!payload || payload.length === 0) {
    return [];
  }
  return payload.map(
    (resp: OrganizationUnitResponse): OrganizationUnit => ({
      CompanyId: resp.CompanyId,
      CompanyName: resp.CompanyName,
      Id: resp.Id,
      Name: resp.Name
    })
  );
};
